import React, { useState, useCallback } from 'react';
import { Plus, Trash2, Download } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { regions, formatPrice } from '../../config/pricingConfig';
import { STORAGE_KEY } from '../../lib/estimateAnalytics';
import { useToast } from '../../components/ui/toast';

const RESOURCE_TYPES = [
  { value: 'compute', label: 'Virtual Machine (Compute)' },
  { value: 'manageddisk', label: 'Managed Disk' },
  { value: 'publicip', label: 'Public IP Address' },
  { value: 'storage', label: 'Storage Account' },
  { value: 'appservice', label: 'App Service Plan' },
  { value: 'sql', label: 'Azure SQL Database' },
];

const HOURS_PER_MONTH = 730;

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Date.now().toString(36) + Math.random().toString(36).slice(2);

const defaultItem = () => ({
  id: newId(),
  type: '',
  region: '',
  sku: '',
  quantity: 1,
  hours: HOURS_PER_MONTH,
  unitPrice: '',
  currency: 'USD',
  unitOfMeasure: '1 Hour',
  description: '',
  manualPrice: false,
});

const ItemRow = ({ item, onChange, onRemove }) => {
  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="p-2">
        <Select value={item.type} onValueChange={(v) => onChange(item.id, 'type', v)}>
          <SelectTrigger className="h-9 text-xs">
            <SelectValue placeholder="Resource type" />
          </SelectTrigger>
          <SelectContent>
            {RESOURCE_TYPES.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="p-2">
        <Select value={item.region} onValueChange={(v) => onChange(item.id, 'region', v)}>
          <SelectTrigger className="h-9 text-xs">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent>
            {regions.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="p-2">
        <Input
          value={item.sku}
          onChange={(e) => onChange(item.id, 'sku', e.target.value)}
          placeholder="e.g. Standard_D2s_v3"
          className="h-9 text-xs"
        />
      </td>
      <td className="p-2 w-20">
        <Input
          type="number"
          min="1"
          value={item.quantity}
          onChange={(e) => onChange(item.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
          className="h-9 text-xs"
        />
      </td>
      <td className="p-2 w-24">
        <Input
          type="number"
          min="0"
          max={HOURS_PER_MONTH}
          value={item.hours}
          onChange={(e) => onChange(item.id, 'hours', Math.max(0, parseFloat(e.target.value) || 0))}
          className="h-9 text-xs"
        />
      </td>
      <td className="p-2 w-28">
        <Input
          type="number"
          min="0"
          step="0.0001"
          value={item.unitPrice}
          onChange={(e) => onChange(item.id, 'unitPrice', e.target.value)}
          placeholder="Unit price"
          className="h-9 text-xs"
        />
      </td>
      <td className="p-2 text-right text-sm font-medium">
        {item.unitPrice
          ? formatPrice(parseFloat(item.unitPrice) * item.quantity * item.hours)
          : '—'}
      </td>
      <td className="p-2 text-center">
        <button
          onClick={() => onRemove(item.id)}
          className="text-red-400 hover:text-red-600 p-1"
          aria-label="Remove row"
        >
          <Trash2 size={16} />
        </button>
      </td>
    </tr>
  );
};

const CostEstimator = () => {
  const [items, setItems] = useState([defaultItem()]);
  const [estimateName, setEstimateName] = useState('My Estimate');
  const toast = useToast();

  const handleChange = useCallback((id, field, value) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  }, []);

  const handleAdd = () => setItems((prev) => [...prev, defaultItem()]);

  const handleRemove = useCallback((id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const totalMonthly = items.reduce((sum, item) => {
    const price = parseFloat(item.unitPrice);
    if (isNaN(price)) return sum;
    return sum + price * item.quantity * item.hours;
  }, 0);

  const handleSave = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const estimate = {
        id: Date.now(),
        name: estimateName,
        items,
        total: totalMonthly,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...saved, estimate]));
      toast.success(`Estimate "${estimateName}" saved!`);
    } catch {
      toast.error('Failed to save estimate. Your browser storage may be full or restricted.');
    }
  };

  const handleExportCsv = () => {
    const header = ['Type', 'Region', 'SKU', 'Quantity', 'Hours/Month', 'Unit Price', 'Monthly Cost'];
    const rows = items.map((item) => {
      const monthlyCost = parseFloat(item.unitPrice) * item.quantity * item.hours;
      return [
        item.type,
        item.region,
        item.sku,
        item.quantity,
        item.hours,
        item.unitPrice,
        isNaN(monthlyCost) ? '' : monthlyCost.toFixed(4),
      ];
    });
    rows.push(['', '', '', '', '', 'TOTAL', totalMonthly.toFixed(4)]);

    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${estimateName.replace(/\s+/g, '_')}_estimate.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Cost Estimator</h2>
          <p className="text-sm text-gray-500 mt-1">
            Build a multi-resource estimate. Enter unit prices manually or look them up
            using the Pricing Tool.
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            value={estimateName}
            onChange={(e) => setEstimateName(e.target.value)}
            placeholder="Estimate name"
            className="w-40 h-9 text-sm"
          />
          <Button onClick={handleSave} className="h-9 text-sm">
            Save
          </Button>
          <Button
            onClick={handleExportCsv}
            className="h-9 text-sm bg-gray-700 hover:bg-gray-800"
          >
            <Download size={14} className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      <div className="overflow-auto rounded-lg border shadow-sm mb-4">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="p-2 text-left">Resource Type</th>
              <th className="p-2 text-left">Region</th>
              <th className="p-2 text-left">SKU / Name</th>
              <th className="p-2 text-left">Qty</th>
              <th className="p-2 text-left">Hrs/Month</th>
              <th className="p-2 text-left">Unit Price ($)</th>
              <th className="p-2 text-right">Monthly Cost</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                onChange={handleChange}
                onRemove={handleRemove}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Button onClick={handleAdd} className="flex items-center gap-2 bg-gray-100 text-gray-800 hover:bg-gray-200">
          <Plus size={16} /> Add Resource
        </Button>

        <Card className="p-4 min-w-[220px]">
          <p className="text-sm text-gray-500">Estimated Monthly Total</p>
          <p className="text-3xl font-bold text-teal-600 mt-1">
            {formatPrice(totalMonthly)}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Based on {HOURS_PER_MONTH} hrs/month
          </p>
        </Card>
      </div>

      <p className="text-xs text-gray-400 mt-6">
        ⚠️ Unit prices shown are estimates. Use the Pricing Tool to retrieve current
        Azure Retail Prices and paste them into the Unit Price column.
      </p>
    </div>
  );
};

export default CostEstimator;
